// Put everything back (#6772, epic #6552; prototype moveReset `:2699`). The prototype restored
// its local map in one go; here it goes through the server's limits like any move, so part of
// it can be refused and resumed (a listed deviation).
//
// THE LIST lives in page memory only (`lib/accepted-move.ts`): each task this viewer moved since
// the page loaded (a 201's `batch.items[].task_id`) and the machine it was on before its FIRST
// move on this page. Where it is NOW is derived from its newest fresh task read
// (`movePlace`, `./move-read.ts`), never from a 201. {@link putBackParts} groups the tasks off
// home by (current machine, home machine) into one move per pair, split into consecutive parts
// of at most `RUNNER_MAX_SLOTS_MAX` items (`MoveItemsWireSchema` answers 400 above it).
//
// ONE PART AT A TIME ({@link runPutBack}), each its own send and `Idempotency-Key`:
// - accepted: its tasks are on their way, not home; they leave the list only once a fresh read
//   shows them landed home, and "all back" is said only then (`lib/accepted-move.ts`);
// - refused for a wait (a 429), for the machine as a whole (no `item`, such as
//   `target_offline`), failed with no readable refusal, or held by the page guard: the run
//   STOPS there, the part stays listed, and the next press resumes from it;
// - refused naming an `item` (every per-job refusal does): the refusal changed nothing, and the
//   run goes on to the next part. Only `item_gone` is permanent (the job finished or left the
//   machine), so only it drops that task from the list (the part's other tasks, which the
//   refusal did not move either, stay listed); any other per-job refusal
//   (`waiting_on_answer`, `still_starting`, `still_moving`, `stop_requested`,
//   `lane_not_pausable`, `cannot_checkpoint`, `capability_off`, `repo_already_there`) can clear,
//   so the task stays listed and the next press retries it.

import { RUNNER_MAX_SLOTS_MAX } from '@developerz/domain';
import type { MoveOutcome } from './move-page';

/** One move of the run: the tasks on `fromId` whose home is `toId`. */
export type PutBackPart = {
  readonly fromId: string;
  readonly toId: string;
  readonly taskIds: readonly string[];
};

/** The run's parts: one move per (current, home) pair, at most eight items each. */
export function putBackParts(
  homes: ReadonlyMap<string, string>,
  where: ReadonlyMap<string, string>,
): readonly PutBackPart[] {
  const pairs = new Map<string, { fromId: string; toId: string; ids: string[] }>();
  for (const [taskId, home] of homes) {
    const now = where.get(taskId);
    if (now === undefined || now === home) continue;
    const key = `${now}>${home}`;
    const pair = pairs.get(key) ?? { fromId: now, toId: home, ids: [] };
    pair.ids.push(taskId);
    pairs.set(key, pair);
  }
  const parts: PutBackPart[] = [];
  for (const p of pairs.values()) {
    for (let i = 0; i < p.ids.length; i += RUNNER_MAX_SLOTS_MAX) {
      parts.push({
        fromId: p.fromId,
        toId: p.toId,
        taskIds: p.ids.slice(i, i + RUNNER_MAX_SLOTS_MAX),
      });
    }
  }
  return parts;
}

export type PutBackResult =
  | { readonly kind: 'done' }
  /** Every part ran, but these tasks were refused for now and stay listed for the next press. */
  | { readonly kind: 'kept'; readonly taskIds: readonly string[] }
  /** Stopped on `part`, which stays listed; `outcome` is why. */
  | { readonly kind: 'stopped'; readonly part: PutBackPart; readonly outcome: MoveOutcome };

/**
 * Send the parts one at a time, each awaited before the next. `landed` runs for an accepted
 * part (its 201: the tasks are sent, not yet home), `dropped` for the one task an `item_gone`
 * refusal names.
 */
export async function runPutBack(
  parts: readonly PutBackPart[],
  send: (part: PutBackPart) => Promise<MoveOutcome>,
  on: {
    readonly landed: (part: PutBackPart) => void;
    readonly dropped: (taskId: string, outcome: MoveOutcome) => void;
  },
): Promise<PutBackResult> {
  const kept: string[] = [];
  for (const part of parts) {
    const outcome = await send(part);
    if (outcome.kind === 'moved') {
      on.landed(part);
      continue;
    }
    const item = outcome.kind === 'refused' ? outcome.refusal.item : undefined;
    if (item === undefined || !part.taskIds.includes(item)) {
      return { kind: 'stopped', part, outcome };
    }
    // A refusal moves nothing: every task of the part is still away from home. Only the one a
    // permanent `item_gone` names leaves the list; the rest stay for the next press.
    const gone = outcome.kind === 'refused' && outcome.refusal.reason === 'item_gone';
    if (gone) on.dropped(item, outcome);
    kept.push(...part.taskIds.filter((id) => !(gone && id === item)));
  }
  // Done only when every task that is not gone landed.
  return kept.length === 0 ? { kind: 'done' } : { kind: 'kept', taskIds: kept };
}
